import { DateTimePickerValueType } from '@nagel-types';
import { DATE_LOCALE } from './applicationDateAdapter';
import {
    END_OF_DAY,
    HOURS,
    MILLISECONDS,
    MILLISECONDS_IN_MINUTE,
    MINUTES,
    MS_TO_HOUR,
    ONE_DAY,
    SECONDS,
    STRING_DATE_BEFORE_TIME_ZONE_LENGTH,
    TIME_ADJUST_NUMBER,
    TIME_SLICE_END_INDEX,
    TIME_SLICE_START_INDEX
} from './dateConsts';

export const getStartOfDay = (date: Date) => {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);

    return start;
};

export const getIsInvalidDate = (date: Date | null | undefined) => {
    return !date || isNaN(date.getTime());
};

export const getEndOfDay = (date: Date) => {
    const { hours, minutes, seconds, milliseconds } = END_OF_DAY;

    const end = new Date(date);
    end.setHours(hours, minutes, seconds, milliseconds);

    return end;
};

export const getTimeOfDay = (date: Date) => {
    return ('00' + date.getHours()).slice(TIME_ADJUST_NUMBER) + ':' + ('00' + date.getMinutes()).slice(TIME_ADJUST_NUMBER);
};

export const getTimeOfDayWithSeconds = (date: Date) => {
    return getTimeOfDay(date) + ':' + ('00' + date.getSeconds()).slice(TIME_ADJUST_NUMBER);
};

export const formatToLocalDate = (date: Date, locale: string = DATE_LOCALE.EN_UK) => {
    return date.toLocaleDateString(locale, { year: 'numeric', month: '2-digit', day: '2-digit' });
};

export const parseToDdMmYyyy = (date: string): Date => {
    const [day, month, year] = date.split('/').map(val => parseInt(val, 10));

    return new Date(year, month - 1, day);
};

export const getTimeZoneOffsetInMs = (date: Date) => {
    return date.getTimezoneOffset() * MILLISECONDS_IN_MINUTE;
};

export const getDateTimeStringWithoutTimezone = (date: string) => {
    return date ? date.slice(0, STRING_DATE_BEFORE_TIME_ZONE_LENGTH) : null;
};

export const getTimezoneAdjustedDate = (date: Date) => {
    const offsetMs = getTimeZoneOffsetInMs(date);

    return new Date(date.getTime() - offsetMs);
};

export const getFormattedTimezoneAdjustedDate = (date: Date) => {
    return getTimezoneAdjustedDate(date).toISOString();
};

export const getDaysDifference = (date1: Date, date2: Date): number => {
    const diffInTime = date2.getTime() - date1.getTime();

    return Math.round(diffInTime / ONE_DAY);
};

export const getDateDifferenceString = (date1: Date, date2: Date): string => {
    const diffMs = getDaysDifference(date1, date2);

    const diffDays = Math.floor(diffMs / ONE_DAY);
    const diffHours = Math.floor(diffMs / (MILLISECONDS * SECONDS * MINUTES)) % HOURS;
    const diffMinutes = Math.floor(diffMs / (MILLISECONDS * SECONDS)) % MINUTES;

    let result = '';

    if (diffDays > 0) {
        result += `${diffDays} d `;
    }

    if (diffHours > 0) {
        result += `${diffHours} h `;
    }
    if (diffMinutes > 0) {
        result += `${diffMinutes} min`;
    }

    if (!result) {
        result = '-';
    }

    return result;
};


export const addTimeToDate = (date: Date, hours: number, minutes: number): Date => {
    const newDate = date;
    newDate.setHours(hours, minutes);

    return newDate;
};

export const convertDateTimePickerValueToDate = (date: string, time: { hours: string; minutes: string }) => {
    const parsedTimeHours = Number(time?.hours);
    const parsedTimeMinutes = Number(time?.minutes);

    return addTimeToDate(new Date(date), parsedTimeHours, parsedTimeMinutes);
};

export const getParsedDateTimePickerDates = (dateTimeRangeValue: DateTimePickerValueType) => {
    const startDate = dateTimeRangeValue?.date?.startDate || new Date();
    const endDate = dateTimeRangeValue?.date?.endDate || startDate;

    const startTime = dateTimeRangeValue?.time?.startTime || {hours: '00', minutes: '00'};
    const endTime = dateTimeRangeValue?.time?.endTime || {hours: '23', minutes: '59'};

    const parsedStartDate = convertDateTimePickerValueToDate(new Date(startDate).toISOString(), startTime);
    const parsedEndDate = convertDateTimePickerValueToDate(new Date(endDate).toISOString(), endTime);

    return { startDate: parsedStartDate, endDate: parsedEndDate };
};

export const getDecimalHourFromDate = (date: Date): number => {
    return date.getHours() + date.getMinutes() / MINUTES;
};

export const getDiffInHours = (
    startDate: Date = new Date(),
    endDate: Date = new Date()
) => {
    const diffInMs = endDate.getTime() - startDate.getTime();
    const diffInHours = diffInMs / MS_TO_HOUR;

    return diffInHours;
};

export const convertToDateOnlyString = (date: Date): string => {
    const formattedDate = getFormattedTimezoneAdjustedDate(date);

    return getDateFromDateTimeString(formattedDate);
};

export const getDateFromDateTimeString = (dateTimeString: string): string => {
    return dateTimeString.split('T')[0];
};

export const getTimeFromDateTimeString = (dateTimeString: string): string => {
    return dateTimeString.split('T')[1].slice(TIME_SLICE_START_INDEX, TIME_SLICE_END_INDEX);
};

export const convertToTimeOnlyString = (date: Date): string => {
    return date.toTimeString().slice(TIME_SLICE_START_INDEX, TIME_SLICE_END_INDEX);
};

export const isDateInRange = (date: Date, start: Date, end: Date) => {
    const d = date.getTime();

    return d >= start.getTime() && d <= end.getTime();
};

export const convertSecondsToMinutes = (seconds: number): number => {
    return Math.floor(seconds / MINUTES);
};

export const getIsAfterOrEqualYear = (date: Date, previousYear: number) => {
    return date.getFullYear() >= previousYear;
};

export const getIsBeforeOrEqualYear = (date: Date, previousYear: number) => {
    return date.getFullYear() <= previousYear;
};

export const getAreDatesEqual = (date1: Date, date2: Date) => {
    return date1.toDateString() === date2.toDateString();
};

export const getAreDateTimesEqual = (date1: Date, date2: Date) => {
    return date1.getTime() === date2.getTime();
};